/**
 * Pure selection-query compilation. (The fork's identifier-only selection read-back,
 * currentSelectionState, lands with its caller, SelectionCmds' GetCurrentSelection.)
 *
 * BFD owns the full `select-atoms` grammar. This module deliberately compiles only the
 * deterministic unary subset that can run over one BMaps atom without a server or 3Dmol.
 * Unsupported clauses fail closed instead of being approximated.
 */
import { getFullResidueId } from './mol_info_utils';

const FIELD_NAMES = new Set([
    'resname', 'chain', 'element', 'elem', 'atom', 'name', 'id', 'uid', 'residue', 'compound',
]);
const SPATIAL_WORDS = new Set(['within', 'around', 'sphere', 'box', 'near']);

function normalize(value) {
    return String(value ?? '').toUpperCase();
}

function ownerOf(atom) {
    return atom?.getAtomGroup?.() || atom?.atomGroup || null;
}

/**
 * The value `id` / `uid` in a selection query compares against.
 *
 * Exported because anything that BUILDS an `id ...` query must use the same definition the
 * parser reads, or it emits a query that silently matches nothing. The chemistry
 * disambiguation resolver (gibbs/knowledge/compoundAtomSelection.js) is the first such caller.
 *
 * @param {object} atom
 * @returns {string|number|null}
 */
export function atomId(atom) {
    return atom?.uniqueID ?? atom?.serial ?? atom?.index ?? null;
}

function residueId(atom) {
    try {
        return getFullResidueId(atom);
    } catch (_) {
        return null;
    }
}

function componentId(component) {
    return component?.resSpec || component?.key || component?.displayName || null;
}

function isLigandAtom(atom) {
    const owner = ownerOf(atom);
    return owner?.isLigand?.() === true || owner?.type === 'Ligand';
}

function fieldPredicate(field, rawValue) {
    const wanted = normalize(rawValue);
    switch (field) {
        case 'resname':
            return (atom) => normalize(atom?.resname) === wanted
                || normalize(atom?.resn) === wanted;
        case 'chain': return (atom) => normalize(atom?.chain) === wanted;
        case 'element':
        case 'elem': return (atom) => normalize(atom?.elem) === wanted;
        case 'atom':
        case 'name': return (atom) => normalize(atom?.atom) === wanted;
        case 'id':
        case 'uid': return (atom) => normalize(atomId(atom)) === wanted;
        case 'residue': return (atom) => normalize(residueId(atom)) === wanted;
        case 'compound': {
            return (atom) => {
                const owner = ownerOf(atom);
                return normalize(componentId(owner)) === wanted
                    || normalize(owner?.resname) === wanted;
            };
        }
        default: throw new TypeError(`Unsupported select-atoms field '${field}'.`);
    }
}

function barePredicate(rawToken) {
    const wanted = normalize(rawToken);
    switch (rawToken.toLowerCase()) {
        case 'all': return () => true;
        case 'none': return () => false;
        case 'protein': return (atom) => !atom?.hetflag && !atom?.hetatm;
        case 'ligand': return isLigandAtom;
        case 'hydrogen': return (atom) => normalize(atom?.elem) === 'H';
        default:
            return (atom) => {
                const owner = ownerOf(atom);
                return normalize(residueId(atom)) === wanted
                    || normalize(componentId(owner)) === wanted
                    || normalize(owner?.resname) === wanted;
            };
    }
}

function tokenize(query) {
    const tokens = query.match(/"(?:\\.|[^"\\])*"|'(?:\\.|[^'\\])*'|\(|\)|[^\s()]+/g) || [];
    return tokens.map((token) => {
        const quoted = (token.startsWith('"') && token.endsWith('"'))
            || (token.startsWith("'") && token.endsWith("'"));
        return quoted ? token.slice(1, -1) : token;
    });
}

/**
 * Compile the non-spatial unary subset of the existing `select-atoms` query language.
 * Boolean precedence is `not`, then `and`, then `or`.
 *
 * @param {string|{query: string}} spec serializable selection specification
 * @returns {(atom: object) => boolean}
 */
export function resolveSelection(spec) {
    let query = typeof spec === 'string' ? spec : spec?.query;
    if (typeof query !== 'string' || !query.trim()) {
        throw new TypeError('selection spec must be a non-empty query string or { query } object.');
    }
    query = query.trim().replace(/^select\s+/i, '');
    const tokens = tokenize(query);
    const spatial = tokens.find((token) => SPATIAL_WORDS.has(token.toLowerCase()));
    if (spatial) {
        throw new TypeError(
            `Unsupported select-atoms clause '${spatial}': spatial queries require BFD context.`
        );
    }

    let index = 0;
    const peek = () => tokens[index];
    const take = () => tokens[index++];

    function primary() {
        const token = take();
        if (!token) throw new TypeError(`Incomplete selection query '${query}'.`);
        if (token === '(') {
            const predicate = orExpr();
            if (take() !== ')') throw new TypeError(`Unclosed parenthesis in selection query '${query}'.`);
            return predicate;
        }
        if (token === ')') throw new TypeError(`Unexpected ')' in selection query '${query}'.`);

        const field = token.toLowerCase();
        if (FIELD_NAMES.has(field)) {
            const value = take();
            if (!value || ['and', 'or', 'not', ')'].includes(value.toLowerCase())) {
                throw new TypeError(`Selection field '${token}' requires a value.`);
            }
            return fieldPredicate(field, value);
        }
        return barePredicate(token);
    }

    function notExpr() {
        if (peek()?.toLowerCase() === 'not') {
            take();
            const predicate = notExpr();
            return (atom) => !predicate(atom);
        }
        return primary();
    }

    function andExpr() {
        let predicate = notExpr();
        while (peek()?.toLowerCase() === 'and') {
            take();
            const right = notExpr();
            const left = predicate;
            predicate = (atom) => left(atom) && right(atom);
        }
        return predicate;
    }

    function orExpr() {
        let predicate = andExpr();
        while (peek()?.toLowerCase() === 'or') {
            take();
            const right = andExpr();
            const left = predicate;
            predicate = (atom) => left(atom) || right(atom);
        }
        return predicate;
    }

    const predicate = orExpr();
    if (index !== tokens.length) {
        throw new TypeError(`Unexpected token '${tokens[index]}' in selection query '${query}'.`);
    }
    return predicate;
}
